/**
 * Hook for acquiring an access token against the configured API scope.
 *
 * Uses MSAL's silent acquisition (iframe flow) first; falls back to popup
 * if a fresh user interaction is required. The frontend never sees a
 * refresh token — that's MSAL's internal cache only (memory, per CLAUDE.md
 * §9.1).
 */

import { useCallback } from "react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useAuthConfig } from "./useAuthConfig";

export function useAccessToken(): () => Promise<string> {
  const { instance, accounts } = useMsal();
  const config = useAuthConfig();

  return useCallback(async () => {
    const account = accounts[0];
    if (!account) {
      throw new Error("Not signed in.");
    }
    try {
      const result = await instance.acquireTokenSilent({
        account,
        scopes: config.scopes,
      });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        const result = await instance.acquireTokenPopup({
          scopes: config.scopes,
        });
        return result.accessToken;
      }
      throw err;
    }
  }, [instance, accounts, config.scopes]);
}
